import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Autocomplete as AutocompleteMui, MenuItem, TextField } from '@mui/material'
import { isArray, isNil, orderBy } from 'lodash'
import { normalizeString } from 'common/utils/StringUtil'
import { hybridSearch } from 'common/utils/SearchUtil'

const DEFAULT_FILTER_KEYS = ['label']

// Si vous voulez ajouter des groupes faire un nouveau composant GroupAutocomplete !!
const Autocomplete = ({
    label = '',
    value = '',
    items = [],
    onChange = () => { },
    disabled = false,
    sortItems = true,
    sortKey = 'label',
    filterKeys = DEFAULT_FILTER_KEYS,
    multiple = false,
    required = false,
}) => {
    if (multiple && !isArray(value)) {
        // eslint-disable-next-line no-console
        console.error('Dans le cas d\'un autocomplete avec valeur multiple, value doit être une liste')
    }
    const selectedItem = multiple ? value.map(v => items.find(i => i.id === v)) : (items.find(i => i.id === value) ?? null)

    const formattedItems = useMemo(() => {
        return items.map(item => ({
            value: item.id ?? item.code ?? item.value,
            label: item.name || item.label || item.title || ' ',
            _normalizedSearchString: filterKeys.map(key => normalizeString(item[key])),
        }))
    }, [items, filterKeys])

    const sortedItems = useMemo(() => {
        if (!sortItems) {
            return formattedItems
        }
        return orderBy(formattedItems, sortKey)
    }, [formattedItems, sortItems, sortKey])

    return (
        <AutocompleteMui
            options={sortedItems}
            value={selectedItem}
            onChange={(_, item) => {
                if (!multiple) return onChange(item?.value)
                if (isNil(item)) return onChange([])
                const isSelected = value.includes(item.value)
                onChange(isSelected ? value.filter(v => v !== item.value) : [...value, item.value])
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    InputProps={{
                        ...params.InputProps,
                        notched: false,
                    }}
                    InputLabelProps={{
                        ...params.InputLabelProps,
                        shrink: false,
                    }}
                    label={required ? `${label} *` : label}
                />
            )}
            renderOption={(props, option, { inputValue }) => (
                <MenuItem
                    {...props}
                    key={option.value}
                    component='li'
                    sx={{
                        paddingLeft: 8,
                        backgroundColor: inputValue.includes(option.label) ? 'rgba(53, 96, 159, 0.12)' : '#FFF',
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                    }}
                >
                    {option.label}
                </MenuItem>
            )}
            getOptionLabel={opt => {
                if (isArray(opt)) return opt.map(o => `${o.label ?? ''}, `).join('')
                return opt.label ?? ''
            }}
            slotProps={{
                paper: {
                    sx: {
                        '& .MuiAutocomplete-listbox': {
                            padding: '4px 0',
                        },
                    },
                },
            }}
            getOptionKey={opt => opt.id}
            isOptionEqualToValue={(option, valueObj) => (isArray(valueObj) ? valueObj.some(v => v?.id === option.value) : option.value === valueObj?.id)}
            filterOptions={(optionsFilter, params) => {
                const inputValue = multiple ? params?.inputValue.slice(params?.inputValue.lastIndexOf(', ') + 2) : params?.inputValue
                return hybridSearch(optionsFilter, normalizeString(inputValue))
            }}
            disableCloseOnSelect={multiple}
            disabled={disabled}
            noOptionsText='Aucun élément'
        />
    )
}

Autocomplete.propTypes = {
    label: PropTypes.string,
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    ]),
    items: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        // + sortKey
        // + name, title, id, code
    })),
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    sortItems: PropTypes.bool,
    sortKey: PropTypes.string,
    filterKeys: PropTypes.arrayOf(PropTypes.string),
    multiple: PropTypes.bool,
    required: PropTypes.bool,
}

export default Autocomplete